import React, { useEffect, useState } from 'react'

import {
  Badge,
  Box,
  Button,
  Card,
  Collapse,
  Grid,
  HStack,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react'
import { CallStart } from 'src/components/icon'
import { AgoraCloudProxyMode } from 'src/lib/gql-client'

import {
  NetworkTestResult,
  NetworkTestState,
  useTwoClientNetworkTest,
} from '../../hooks/useTwoClientNetworkTest'

// 0: unknown, 1: excellent, 2: good, 3: slightly impaired,
// 4: not very smooth, 5: very poor, 6: disconnected
const qualityBadge = (
  score: 0 | 1 | 2 | 3 | 4 | 5 | 6,
): { label: string; colorScheme: string } => {
  if (score === 0) return { label: '不明', colorScheme: 'gray' }
  if (score <= 2) return { label: '良好', colorScheme: 'green' }
  if (score === 3) return { label: 'やや不安定', colorScheme: 'yellow' }
  return { label: '不安定', colorScheme: 'red' }
}

const StatsRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <>
    <Text fontSize="sm" color="gray.600">
      {label}:
    </Text>
    <Text>{value}</Text>
  </>
)

type NetworkTestDoneContentProps = {
  result: NetworkTestResult
  showDetails: boolean
  onToggleDetails: () => void
  onRetest: () => void
}

const NetworkTestDoneContent: React.FC<NetworkTestDoneContentProps> = ({
  result,
  showDetails,
  onToggleDetails,
  onRetest,
}) => {
  const uplink = qualityBadge(result.uplinkQuality)
  const downlink = qualityBadge(result.downlinkQuality)
  return (
    <VStack align="start" spacing="2">
      <HStack spacing="6">
        <HStack spacing="1">
          <Text fontSize="sm" color="gray.600">
            送信品質:
          </Text>
          <Badge colorScheme={uplink.colorScheme}>{uplink.label}</Badge>
        </HStack>
        <HStack spacing="1">
          <Text fontSize="sm" color="gray.600">
            受信品質:
          </Text>
          <Badge colorScheme={downlink.colorScheme}>{downlink.label}</Badge>
        </HStack>
      </HStack>
      <Link
        fontSize="xs"
        color="blue.500"
        onClick={onToggleDetails}
        cursor="pointer"
      >
        {showDetails ? '詳細を隠す' : '詳細を見る'}
      </Link>
      <Collapse in={showDetails} animateOpacity>
        <Grid pt="1" templateColumns="max-content 1fr" columnGap="8" rowGap="2">
          <StatsRow
            label="パケットロス率"
            value={`${(result.packetLossRate * 100).toFixed(1)}%`}
          />
          <StatsRow
            label="直近パケットロス率"
            value={`${(result.currentPacketLossRate * 100).toFixed(1)}%`}
          />
          <StatsRow
            label="ビットレート"
            value={`${(result.receiveBitrate / 1000).toFixed(0)} kbps`}
          />
          <StatsRow
            label="フリーズ率"
            value={`${(result.freezeRate * 100).toFixed(1)}%`}
          />
          <StatsRow label="伝送遅延" value={`${result.transportDelay} ms`} />
          <StatsRow
            label="エンドツーエンド遅延"
            value={`${result.end2EndDelay} ms`}
          />
          {result.codecType && (
            <StatsRow label="コーデック" value={result.codecType} />
          )}
        </Grid>
      </Collapse>
      <Button variant="outline" onClick={onRetest} colorScheme="blue" mt="3">
        再測定する
      </Button>
    </VStack>
  )
}

type Props = {
  roomUid: string
  outboundToken: string
  outboundUid: number
  inboundToken: string
  inboundUid: number
  agoraCloudProxyMode: AgoraCloudProxyMode
  onStateChange: (state: NetworkTestState) => void
  onResult: (result: NetworkTestResult | null) => void
}

export const NetworkTestPanel: React.FC<Props> = ({
  roomUid,
  outboundToken,
  outboundUid,
  inboundToken,
  inboundUid,
  agoraCloudProxyMode,
  onStateChange,
  onResult,
}) => {
  const [showDetails, setShowDetails] = useState(false)
  const { testState, result, startTest } = useTwoClientNetworkTest({
    roomUid,
    outboundToken,
    outboundUid,
    inboundToken,
    inboundUid,
    agoraCloudProxyMode,
  })

  const handleStart = async () => {
    onStateChange('testing')
    onResult(null)
    await startTest()
    // startTest 完了後に state/result の最新値を伝える
    // useEffect で同期する方が確実なため、ここでは呼ばない
  }

  // testState / result の変化を親に通知
  useEffect(() => {
    onStateChange(testState)
  }, [testState, onStateChange])

  useEffect(() => {
    onResult(result)
  }, [result, onResult])

  return (
    <Card p="4">
      <VStack spacing="4" align="start">
        <HStack spacing="2">
          <Box color="gray.700">
            <CallStart />
          </Box>
          <Text fontWeight="bold" fontSize="md">
            ネットワーク接続
          </Text>
        </HStack>

        {testState === 'idle' && (
          <Button variant="outline" onClick={handleStart} colorScheme="blue">
            接続テストを開始する
          </Button>
        )}

        {testState === 'testing' && (
          <Button
            variant="outline"
            isLoading
            loadingText="測定中..."
            colorScheme="blue"
          />
        )}

        {testState === 'error' && (
          <VStack align="start" spacing="2">
            <Text fontSize="sm" color="red.500">
              接続テストに失敗しました
            </Text>
            <Button
              variant="outline"
              onClick={handleStart}
              colorScheme="blue"
              size="sm"
            >
              再試行する
            </Button>
          </VStack>
        )}

        {testState === 'done' && result && (
          <NetworkTestDoneContent
            result={result}
            showDetails={showDetails}
            onToggleDetails={() => setShowDetails(v => !v)}
            onRetest={handleStart}
          />
        )}
      </VStack>
    </Card>
  )
}
